"use client";

import ScriptSnapshotDiff from "./ScriptSnapshotDiff";
import {useId, useState} from "react";
import Icon from "@/components/library/Icon";
import {useTranslation} from "react-i18next";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";

interface ToolApprovalCardProps {
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<void>;
}

export default function ToolApprovalCard({call, completed, result, active, disabled, onRespond}: ToolApprovalCardProps) {
   const {t} = useTranslation("ask");
   const detailsId = useId();
   const [detailsExpanded, setDetailsExpanded] = useState(false);
   const {status, settled, expired, error, canRespond, canApply, respond} =
      useToolApprovalDecision({call, completed, result, active, disabled, onRespond});
   const approval = call.approval;
   if (!approval) return null;

   const reviewed = !!approval.proposal || !!approval.previewUnavailable;
   const collapsible = !reviewed && !error && status !== "failed" && status !== "indeterminate" &&
      (approval.approved === true || status === "completed");
   const expanded = !collapsible || detailsExpanded;
   const heading = <>
      <output className="block text-xs font-medium uppercase tracking-wider text-500">
         {t(`toolApproval.${status}`)}
      </output>
      <span className="mt-1 block break-words text-sm font-semibold text-800">{approval.proposal?.summary ?? call.functionName}</span>
   </>;

   return (
      <section aria-label={t("toolApproval.title")} className="my-3 overflow-hidden rounded-xl border border-neutral bg-background">
         {collapsible ? (
            <button type="button" aria-expanded={expanded} aria-controls={detailsId}
               onClick={() => setDetailsExpanded(value => !value)}
               className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left hover:bg-neutral/20 focus-visible:outline focus-visible:outline-2 ${expanded ? "border-b border-neutral" : ""}`}>
               <span className="min-w-0 flex-1">{heading}</span>
               <Icon icon={expanded ? "chevron-down" : "chevron-right"} className="h-3 w-3 shrink-0 text-500" />
            </button>
         ) : <div className="border-b border-neutral px-4 py-3">{heading}</div>}
         <div id={detailsId} hidden={!expanded}>
            {approval.proposal ? <ScriptSnapshotDiff preview={approval.proposal} /> : !approval.previewUnavailable && <div className="px-4 py-3">
               <p className="mb-2 text-xs text-500">{t("toolApproval.arguments")}</p>
               <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-neutral/30 p-3 text-xs leading-relaxed text-700">
                  {JSON.stringify(call.parameters, null, 2)}
               </pre>
            </div>}
            <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error}
               previewUnavailable={approval.previewUnavailable} className="mx-4 my-3 text-sm" />
            {!settled && active && (
               <div className="sticky bottom-0 border-t border-neutral bg-background px-4 pb-4 pt-1">
                  {!approval.previewUnavailable && <p className="mt-3 text-xs leading-relaxed text-500">
                     {t(approval.proposal ? "toolApproval.previewDescription" : "toolApproval.description")}
                  </p>}
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                     <button type="button" disabled={!canRespond} onClick={() => void respond(false)}
                        className="rounded-lg border border-neutral px-4 py-2 text-sm font-medium text-700 hover:bg-neutral/40 focus-visible:outline focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50">
                        {t(reviewed ? "toolApproval.discard" : "toolApproval.deny")}
                     </button>
                     <button type="button" disabled={!canApply} onClick={() => void respond(true)}
                        className="rounded-lg bg-800 px-4 py-2 text-sm font-medium text-background hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50">
                        {t(reviewed ? "toolApproval.apply" : "toolApproval.approve")}
                     </button>
                  </div>
               </div>
            )}
         </div>
      </section>
   );
}
